import { useI18n } from "../../lib/i18n";

const TONES = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200", warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-red-50 text-red-700 border-red-200", info: "bg-blue-50 text-blue-700 border-blue-200", neutral: "bg-slate-100 text-slate-600 border-slate-200",
  indigo: "bg-sky-50 text-sky-800 border-sky-200", orange: "bg-orange-50 text-orange-700 border-orange-200",
};
const MAP = {
  hadir: "success", izin: "info", sakit: "warning", alfa: "danger", active: "success", inactive: "neutral", completed: "info",
  pending_approval: "warning", approved: "info", issued: "success", rejected: "danger", draft: "neutral", pending: "warning", deleted: "danger",
  critical: "danger", warning: "warning", info: "info", not_started: "neutral", in_progress: "warning",
  teacher: "indigo", instructor: "success", super_admin: "danger", academic_admin: "info", student: "neutral", manager: "warning",
  theory: "indigo", practice: "success", attitude: "warning", physical: "info", lulus: "success", belum: "neutral",
  selected: "success", free: "info", alumni: "orange", passed: "success", failed: "danger", incomplete: "neutral", open: "success", locked: "warning", closed: "neutral",
};

export function StatusBadge({ value, label, tone, testId }) {
  const { t } = useI18n();
  const k = tone || MAP[value] || "neutral";
  const txt = label ?? (t(`roles.${value}`) !== `roles.${value}` ? t(`roles.${value}`) : (t(value) !== value ? t(value) : value));
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[k]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />{txt}
    </span>
  );
}

export function PointsBadge({ points }) {
  const tone = points >= 80 ? "success" : points >= 60 ? "warning" : "danger";
  return <span className={`font-mono text-sm font-semibold rounded-md border px-2 py-0.5 ${TONES[tone]}`}>{points}</span>;
}

export function Hint({ text, testId }) {
  return <span data-testid={testId} title={text} aria-label={text} className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-bold text-slate-400 hover:border-sky-500 hover:text-sky-700 cursor-help select-none">i</span>;
}

export function Skeleton({ className = "" }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />;
}

export function EmptyState({ icon: Icon, title, hint, action, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-8 text-center">
      {Icon && <span className="mb-2 grid h-10 w-10 place-items-center rounded-full bg-white border border-slate-200 text-slate-400"><Icon className="h-5 w-5" /></span>}
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      {hint && <p className="mt-1 text-xs text-slate-500 max-w-xs">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "slate", testId, sub, trend, hint }) {
  const color = { slate: "text-slate-700 bg-slate-100", indigo: "text-sky-800 bg-sky-50", blue: "text-sky-800 bg-sky-50", emerald: "text-emerald-700 bg-emerald-50", amber: "text-amber-700 bg-amber-50", red: "text-red-700 bg-red-50", orange: "text-orange-700 bg-orange-50" }[tone] || "text-slate-700 bg-slate-100";
  const bar = { emerald: "bg-emerald-500", amber: "bg-amber-400", red: "bg-red-500", orange: "bg-orange-500", slate: "bg-slate-300" }[tone] || "bg-[color:var(--brand-blue)]";
  const tr = trend && trend.delta != null ? (trend.delta > 0 ? ["▲", "text-emerald-700 bg-emerald-50 border-emerald-200"] : trend.delta < 0 ? ["▼", "text-rose-700 bg-rose-50 border-rose-200"] : ["•", "text-slate-600 bg-slate-100 border-slate-200"]) : null;
  return (
    <div className="surface relative overflow-hidden p-4 reveal hover:border-sky-300 transition-colors" data-testid={testId}>
      <span className={`absolute inset-x-0 top-0 h-1 ${bar}`} />
      <div className="flex items-start justify-between gap-2">
        <p className="caption flex items-center gap-1.5 leading-tight text-[11px] sm:text-xs">{label}{hint && <Hint text={hint} />}</p>
        {Icon && <span className={`rounded-lg p-2 shrink-0 ${color}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <p className="mt-1.5 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 font-heading">{value ?? "-"}</p>
      {(sub || tr) && <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {tr && <span data-testid={testId ? `${testId}-trend` : undefined} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold ${tr[1]}`}>{tr[0]} {Math.abs(trend.delta)}{trend.unit || ""}{trend.label && <span className="font-normal opacity-80">{trend.label}</span>}</span>}
        {sub && <span>{sub}</span>}
      </div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-heading" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {(actions || action) && <div className="flex flex-wrap gap-2">{actions || action}</div>}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}
